/**
 * The operational, inventory, tools and integration reports (docs/25 —
 * the `oprep` gap rows).
 *
 * Kept out of registry.ts so the definitions that follow the reference's
 * columns, filters and DETAILS modals line for line sit beside each other,
 * and so the project reports can move in registry.ts without touching these.
 */
import { DOCUMENTS } from '../documents/registry.js'
import { SYNCABLE_DOC_TYPES } from '../integration/registry.js'
import {
  defineReport, rc, rf,
  type ReportColumnDef, type ReportColumnDrillDef, type ReportFilterDef, type ReportTone,
} from './types.js'

/* ---------------------------------------------------------------- helpers */

const labelOf = (key: string): string => DOCUMENTS.find((d) => d.key === key)?.label ?? key

/** The DETAILS button on every row (RPT-017). */
const details = (label = 'Details'): ReportColumnDef =>
  ({ key: 'details', label, type: 'details', width: 128, filter: false })

const qty = (key: string, label: string, extra: Partial<ReportColumnDef> = {}): ReportColumnDef =>
  ({ ...rc.qty(key, label), ...extra })

/** A quantity whose cell opens the documents behind it (RPT-017). */
const drillQty = (
  key: string, label: string, docTypes: readonly string[], states?: ReportColumnDrillDef['states'],
  extra: Partial<ReportColumnDef> = {},
): ReportColumnDef =>
  ({ ...rc.qty(key, label), drill: { docTypes, ...(states ? { states } : {}), label: `${label} — details` }, ...extra })

const rate = (key: string, label: string): ReportColumnDef =>
  ({ key, label, type: 'rate', width: 130, align: 'right' })

const pick = (key: string, label: string, width = 150): ReportColumnDef =>
  ({ ...rc.text(key, label, width), filter: 'pick' })

/** Billable / Non billable — the TYPE column the reference filters by pick. */
const billableType = (): ReportColumnDef => pick('billableType', 'Type', 140)

const PROJECT_STATUS_TONES: Readonly<Record<string, ReportTone>> = {
  Ongoing: 'good', 'Not ongoing': 'muted', Completed: 'info', 'On hold': 'warn', Closed: 'muted',
}
const projectStatus = (extra: Partial<ReportColumnDef> = {}): ReportColumnDef =>
  ({ ...rc.text('projectStatus', 'Project status', 150), tones: PROJECT_STATUS_TONES, ...extra })

/** A date range under keys of its own, beside the document date (RPT-016). */
const keyedRange = (key: string, label: string, from: string, to: string): ReportFilterDef =>
  ({ key, label, control: 'dateRange', range: { from, to }, defaultPeriod: 'none' })

const select = (
  key: string, label: string, options: ReadonlyArray<[string, string]>,
  extra: Partial<ReportFilterDef> = {},
): ReportFilterDef =>
  ({ key, label, control: 'select', options: options.map(([value, l]) => ({ value, label: l })), ...extra })

/** Pending (the default) or everything — Material pending, Labour pending (RPT-085). */
const pendingOrAll = (label = 'Status'): ReportFilterDef =>
  select('show', label, [['PENDING', 'Pending'], ['ALL', 'All']], { required: true, defaultValue: 'PENDING' })

const lookup = (key: string, label: string, entity: string, extra: Partial<ReportFilterDef> = {}): ReportFilterDef =>
  ({ key, label, control: 'lookup', lookup: { entity }, ...extra })

const subcontractor = (required = false): ReportFilterDef =>
  lookup('supplierId', 'Subcontractor', 'subcontractor', { required })

/** Picked from the Brand master; runners match its name on the line's brand (REP3-008). */
const brand = (): ReportFilterDef => lookup('brand', 'Brand', 'brand')

const item = (extra: Partial<ReportFilterDef> = {}): ReportFilterDef =>
  lookup('itemId', 'Material', 'item', extra)

/* ================================================================ Project */

export const projectExpenses = defineReport({
  key: 'project_expenses', route: 'project_expenses_itemised', group: 'Project',
  label: 'Project expenses itemised',
  blurb: 'Every cost incurred on a project, itemised against the BOQ and the estimate.',
  // No dates is the whole life of the project; a From date asks for a To date (REP1-005).
  filters: [
    rf.project(true), rf.includeSub(),
    { ...rf.fromTo({ requireFrom: false, requireTo: false, toFollowsFrom: true }, false), defaultPeriod: 'none' },
  ],
  columns: [
    { ...rc.text('boqRef', 'BOQ ref no', 130), frozen: true },
    rc.text('boqDescription', 'BOQ', 240), billableType(),
    rc.text('itemName', 'Item', 220), pick('itemType', 'Item type', 130),
    rc.text('description', 'Description', 240),
    rc.qty('quantity', 'Quantity'), rc.text('unit', 'Unit', 90), rate('rate', 'Rate'),
    rc.money('amount', 'Amount'),
    rc.text('supplierName', 'Supplier', 220),
    rc.text('supplierInvoiceNo', 'Supplier invoice no', 170), rc.date('supplierInvoiceDate', 'Supplier invoice date'),
    rc.doc(), rc.date('documentDate', 'Document date'), pick('docTypeLabel', 'Document type', 180),
    rc.text('estimateHead', 'Lumpsum / estimation', 220),
  ],
  drill: { by: 'document', key: 'id', label: 'The document behind this' },
  help: [
    'Rows are cost actually incurred: approved purchase invoices, other costs and expense reports, and material returned from site to store as a negative. Orders are commitments and are not listed, so the amount total is what the project has cost — the same figure as Incurred on the cost variance report.',
    'BOQ ref, BOQ and Lumpsum / estimation are the estimate line the cost was ultimately raised from, followed back through the receipt, the order and the request.',
  ].join('\n\n'),
} as const)

/* ============================================================== Operation */

export const materialPendingRequisition = defineReport({
  key: 'material_pending_requisition', route: 'mat_pend_mr', group: 'Operation',
  label: 'Material pending for requisition',
  blurb: 'Estimated material that nobody has requested yet.',
  filters: [rf.project(true), rf.includeSub(), pendingOrAll(), rf.category()],
  columns: [
    { ...rc.text('boqRef', 'Ref no', 120), frozen: true },
    rc.text('itemName', 'Item', 220), rc.text('description', 'Description', 240), rc.text('unit', 'Unit', 90),
    rc.qty('estimatedQty', 'Estimated qty'), rc.qty('requestedQty', 'Requested / ordered qty'),
    pick('rateOnly', 'Rate only', 110), rc.qty('pendingQty', 'Pending qty'),
    billableType(), rc.text('boqDescription', 'BOQ', 240), projectStatus(),
  ],
} as const)

export const labourPendingSco = defineReport({
  key: 'labour_pending_sco', route: 'lab_pend_sco', group: 'Operation',
  label: 'Labour pending for SCO',
  blurb: 'Estimated installation work not yet placed on a subcontract order.',
  filters: [rf.project(true), rf.includeSub(), pendingOrAll()],
  columns: [
    { ...rc.text('boqRef', 'Ref no', 120), frozen: true },
    rc.text('itemName', 'Item', 220), rc.text('description', 'Description', 240), rc.text('unit', 'Unit', 90),
    rc.qty('estimatedQty', 'Estimated qty'), rc.qty('orderedQty', 'Ordered qty'),
    pick('rateOnly', 'Rate only', 110), rc.qty('pendingQty', 'Pending qty'),
    billableType(), rc.text('boqDescription', 'BOQ', 240), projectStatus(),
    { ...rc.money('pendingValue', 'Pending value'), optional: true },
  ],
} as const)

const PURCHASE_DOCS = ['purchase_requisition', 'purchase_order'] as const

export const materialRequestExecution = defineReport({
  key: 'material_request_execution', route: 'mat_req_exec_status', group: 'Operation',
  label: 'Material request execution',
  blurb: 'What has happened to each material request line since it was raised.',
  filters: [
    // Opens on Open without a required mark; a blank runs as Open (REP2-003).
    select('completionStatus', 'Completion status', [['OPEN', 'Open'], ['ALL', 'All']], { defaultValue: 'OPEN' }),
    // A job type spans projects, so it switches the project off (REP2-003).
    { ...rf.jobType(), multiple: true, disables: ['projectId', 'includeSubProjects'] },
    { ...rf.project(), multiple: true }, rf.includeSub(),
    lookup('materialRequestId', 'Material request', 'material_request', { multiple: true }),
    keyedRange('approvedDate', 'Approved date', 'approvedFrom', 'approvedTo'),
    { ...rf.period('Document date', false), defaultPeriod: 'none' },
  ],
  columns: [
    { ...rc.doc('documentNo', 'Mat req no'), frozen: true, locked: true },
    rc.date('documentDate', 'Document date'), rc.project(), billableType(),
    rc.text('itemName', 'Material', 220), rc.text('unit', 'Unit', 90), rc.text('brand', 'Brand', 140),
    rc.qty('requestedQty', 'Requested qty'),
    drillQty('orderedQty', 'Ordered', PURCHASE_DOCS, ['APPROVED'], { group: 'Purchase' }),
    drillQty('inReviewOrderQty', 'In review - order', PURCHASE_DOCS, ['IN_REVIEW', 'DRAFT'], { group: 'Purchase' }),
    drillQty('inReviewGrnQty', 'In review GRN', ['goods_receipt'], ['IN_REVIEW', 'DRAFT'], { group: 'Purchase' }),
    drillQty('receivedQty', 'Received (GRN)', ['goods_receipt'], ['APPROVED'], { group: 'Purchase' }),
    drillQty('transferredQty', 'Transferred', ['material_transfer'], ['APPROVED'], { group: 'Transfer' }),
    drillQty('transferInReviewQty', 'In review', ['material_transfer'], ['IN_REVIEW', 'DRAFT'], { group: 'Transfer' }),
    rc.qty('preClosedQty', 'Pre-closed qty'), rc.qty('openQty', 'Total open qty'),
    rc.text('jobTypeName', 'Job type', 160), rc.text('boqRef', 'BOQ ref', 120),
    details(),
    { ...rc.date('approvedDate', 'Approved date'), optional: true },
    { ...rc.date('requiredDate', 'Required date'), optional: true },
    projectStatus({ optional: true }),
    { ...rc.text('description', 'Description', 240), optional: true },
    { ...rc.text('model', 'Model', 140), optional: true },
  ],
  drill: {
    by: 'line', key: 'lineId',
    docTypes: ['purchase_requisition', 'purchase_order', 'goods_receipt', 'material_transfer'],
    label: 'Material request details',
  },
  // Nothing is mandatory any more, but the reference still waits for Apply.
  runOnOpen: false,
  help: [
    'One row per material request line. Ordered is approved orders (directly, or through a purchase requisition); In review - order is orders still being drafted or approved. Received and In review GRN are the receipt notes against those orders. Transferred is material sent from stock against the request.',
    'Total open qty = requested − (ordered + in review orders) − (transferred + transfers in review) − pre-closed. Completion status Open shows only lines with something still open.',
  ].join('\n\n'),
} as const)

export const purchaseOrderExecution = defineReport({
  key: 'purchase_order_execution', route: 'pur_ord_execution', group: 'Operation',
  label: 'Purchase order execution',
  blurb: 'How much of each order line has arrived, and how much has been invoiced.',
  filters: [
    { ...rf.project(), multiple: true }, rf.includeSub(), rf.supplier(),
    lookup('purchaseOrderId', 'Purchase order', 'purchase_order', { multiple: true }),
    rf.period('Ordered between', false),
  ],
  columns: [
    { ...rc.doc('documentNo', 'Pur ord no'), frozen: true, locked: true },
    rc.date('documentDate', 'Pur ord date'), rc.date('deliveryDate', 'Delivery date'),
    rc.text('supplierName', 'Supplier', 220),
    { ...rc.text('materialRequestNo', 'Material request', 150), type: 'docno', link: { to: 'document', idKey: 'materialRequestId' } },
    rc.text('supplierContact', 'Supplier contact', 170), rc.text('contactMobile', 'Contact mobile', 140),
    rc.project(), rc.text('purchaseExecutiveName', 'Purchase executive', 180), billableType(),
    rc.text('itemName', 'Item', 220), pick('itemCategory', 'Item category', 160),
    rc.text('description', 'Description', 220), rc.text('unit', 'Unit', 90),
    rc.qty('orderedQty', 'Ordered qty'), rate('rate', 'Rate'), rc.money('amount', 'Amount'),
    rc.text('tax', 'Tax', 120),
    drillQty('inReviewGrnQty', 'In review - GRN', ['goods_receipt'], ['IN_REVIEW', 'DRAFT']),
    drillQty('receivedQty', 'Received (GRN)', ['goods_receipt'], ['APPROVED']),
    drillQty('invoicedQty', 'Invoiced qty', ['purchase_invoice']),
    rc.qty('grnPendingQty', 'GRN pending qty'),
    rc.text('jobTypeName', 'Job type', 160), rc.text('boqRef', 'BOQ ref', 120),
    details(),
    { ...rc.money('openValue', 'Open value'), optional: true },
  ],
  drill: {
    by: 'line', key: 'lineId', docTypes: ['goods_receipt', 'purchase_invoice'],
    label: 'Receipt note (GRN) / invoice details',
    columns: [
      { key: 'docTypeLabel', label: 'Document type' }, { key: 'documentNo', label: 'Document no' },
      { key: 'documentStatus', label: 'Document status' }, { key: 'documentDate', label: 'Document date' },
      { key: 'quantity', label: 'Quantity' },
    ],
  },
  help: 'GRN pending qty is the ordered quantity less what has been received or is in review on a receipt note, and less anything pre-closed. Invoiced qty counts purchase invoices raised against the order or against its receipt notes.',
} as const)

export const subcontractExecution = defineReport({
  key: 'subcontract_execution', route: 'sub_ord_exec', group: 'Operation',
  label: 'Subcontract order execution',
  blurb: 'Progress recorded and invoiced against each subcontract order line.',
  filters: [
    { ...rf.project(), multiple: true }, rf.includeSub(), subcontractor(),
    lookup('subcontractOrderId', 'Subcontract order', 'subcontract_order', { multiple: true }),
    rf.period('Ordered between', false),
  ],
  columns: [
    { ...rc.doc('documentNo', 'Sub ord no'), frozen: true, locked: true },
    rc.date('documentDate', 'Sub ord date'), rc.date('dueDate', 'Due date'),
    rc.text('supplierName', 'Supplier', 220), rc.project('projectCode', 'Project no'),
    rc.text('purchaseExecutiveName', 'Purchase executive', 180),
    rc.text('itemName', 'Item', 220), rc.text('description', 'Description', 220), rc.text('unit', 'Unit', 90),
    rc.qty('orderedQty', 'Ordered qty'), rate('rate', 'Rate'), rc.money('amount', 'Amount'),
    drillQty('completedQty', 'Completed qty', ['work_progress']),
    drillQty('invoicedQty', 'Invoiced qty', ['purchase_invoice']),
    rc.text('jobTypeName', 'Job type', 160), rc.text('boqRef', 'BOQ ref', 120),
    details(),
    { ...rc.qty('openQty', 'Remaining'), optional: true },
    { ...rc.money('openValue', 'Value remaining'), optional: true },
  ],
  drill: {
    by: 'line', key: 'lineId', docTypes: ['work_progress', 'purchase_invoice'],
    label: 'Completed and invoiced qty details',
    columns: [
      { key: 'docTypeLabel', label: 'Document type' }, { key: 'documentNo', label: 'Document no' },
      { key: 'documentDate', label: 'Document date' }, { key: 'reference', label: 'Supplier ref' },
      { key: 'completedQty', label: 'Completed qty' }, { key: 'invoicedQty', label: 'Invoiced qty' },
    ],
  },
} as const)

export const materialProgress = defineReport({
  key: 'material_progress', route: 'material_progress', group: 'Operation',
  label: 'Material progress tracker',
  blurb: 'Estimated, requested, ordered, received and issued — for every material on a project.',
  filters: [
    rf.project(true), rf.includeSub(),
    select('view', 'Mode', [['summary', 'Summary'], ['detailed', 'Detailed']], { required: true, defaultValue: 'summary' }),
    rf.category(),
  ],
  columns: [
    rc.text('itemName', 'Material', 240), rc.qty('estimatedQty', 'Estimated'),
    rc.qty('requestedQty', 'Requested'), rc.qty('orderedQty', 'Ordered'),
    rc.qty('receivedQty', 'Received'), rc.qty('issuedQty', 'Issued to site'),
  ],
  viewBy: 'view',
  views: [
    { key: 'summary', label: 'Summary' },
    {
      key: 'detailed', label: 'Detailed',
      columns: [
        { ...rc.text('itemName', 'Material', 240), frozen: true },
        rc.doc(), rc.date('documentDate', 'Date'), pick('docTypeLabel', 'Document type', 170), rc.project(),
        rc.text('unit', 'Unit', 90),
        rc.qty('estimatedQty', 'Estimated', true), rc.qty('requestedQty', 'Requested', true),
        rc.qty('orderedQty', 'Ordered', true), rc.qty('receivedQty', 'Received', true),
        rc.qty('issuedQty', 'Issued to site', true),
      ],
    },
  ],
  help: 'Summary is one row per material. Detailed lists every estimate, request, order, receipt and transfer line behind those figures, each in the column it counts towards.',
} as const)

export const materialIssuedToSubcontractor = defineReport({
  key: 'material_issued_subcontractor', route: 'mat_issue_sco', group: 'Operation',
  label: 'Material issued to subcontractor',
  blurb: 'What has been handed to each subcontractor, for recovery against their bill.',
  filters: [
    // From date *, To date optional, both opening empty (REP2-004, REP2-011).
    subcontractor(true), { ...rf.fromTo({ requireTo: false }), defaultPeriod: 'none' },
    rf.project(), rf.includeSub(), { ...rf.category(), multiple: true },
  ],
  columns: [
    rc.date('documentDate', 'Date'), rc.doc(), rc.text('supplierName', 'Subcontractor', 220), rc.project(),
    rc.text('itemName', 'Material', 220), pick('itemCategory', 'Item category', 160),
    rc.qty('quantity', 'Issued'), rc.text('unit', 'Unit', 90), rate('rate', 'Rate'), rc.money('value', 'Value'),
  ],
} as const)

export const deliveryChallanStatus = defineReport({
  key: 'delivery_challan_status', route: 'del_cln_status', group: 'Operation',
  label: 'Delivery challan status',
  blurb: 'Material delivered to site against what has been challaned to the customer.',
  // Project *, then From date optional and To date * (REP2-004, REP2-005).
  filters: [
    { ...rf.project(true), multiple: true }, rf.includeSub(),
    { ...rf.fromTo({ requireFrom: false }), defaultPeriod: 'none' },
  ],
  columns: [
    rc.project('projectCode', 'Project no'), pick('docTypeLabel', 'Document type', 170),
    { ...rc.doc(), frozen: false }, rc.date('documentDate', 'Document date'),
    rc.text('itemName', 'Item', 220), rc.text('brand', 'Brand', 140), rc.text('unit', 'Unit', 90),
    rc.qty('deliveredQty', 'Delivered qty', true),
    drillQty('challanedQty', 'Challaned qty', ['delivery_challan'], undefined, { total: true }),
    rc.qty('pendingQty', 'Still to challan', true),
    rate('rate', 'Rate'), rc.money('amount', 'Amount'),
    details(),
    { ...rc.money('deliveredAmount', 'Delivered amount'), optional: true },
  ],
  drill: { by: 'line', key: 'lineId', docTypes: ['delivery_challan', 'goods_receipt'], label: 'Challans against this delivery' },
  help: 'One row per line delivered to site: a purchase order received at site, or a transfer from the warehouse or another site. Delivered is what reached site (the approved receipts against an order, or the transfer itself); Challaned is the delivery challans raised against it or against its receipts. Amount is the challaned quantity at its rate; Delivered amount (optional) is the delivered quantity at it.',
} as const)

export const purchaseSavings = defineReport({
  key: 'purchase_savings', route: 'purchase_savings', group: 'Operation',
  label: 'Purchase savings',
  blurb: 'Shows amount saved by purchase executives on each purchase. Also shows estimated and ordered amount.',
  // One order is its own answer: it switches off the other order and the
  // dates (REP2-002). From date * and To date *, opening on the FY (REP2-004).
  filters: [
    { ...rf.project(), multiple: true }, rf.includeSub(),
    lookup('purchaseOrderId', 'Purchase order', 'purchase_order', { disables: ['subcontractOrderId', 'period'] }),
    lookup('subcontractOrderId', 'Subcontract order', 'subcontract_order', { disables: ['purchaseOrderId', 'period'] }),
    rf.fromTo(),
    lookup('purchaseExecutiveId', 'Purchase executive', 'employee'),
    rf.supplier(),
  ],
  // Detailed carries every column; Summary is its own table (RPT-105).
  columns: [
    { ...rc.doc(), frozen: true }, rc.date('documentDate', 'Document date'),
    rc.text('supplierName', 'Supplier', 220), rc.project(),
    // MATERIAL/LABOUR is the item itself, as the reference fills it (REP3-001).
    rc.text('purchaseExecutiveName', 'Purchase executive', 200), rc.text('itemName', 'Material / labour', 240),
    rc.text('description', 'Description', 220), rc.text('unit', 'Unit', 90),
    rc.qty('quantity', 'Ordered qty'), rate('estimatedRate', 'Estimated rate'), rate('orderedRate', 'Ordered rate'),
    rc.money('estimatedAmount', 'Estimated amount'), rc.money('orderedAmount', 'Ordered amount'),
    rc.signed('saving', 'Saved amount'), rc.text('boqRef', 'BOQ ref no', 120),
    { ...pick('itemType', 'Type', 130), optional: true },
  ],
  views: [
    {
      key: 'summary', label: 'Summary',
      columns: [
        { ...rc.text('purchaseExecutiveName', 'Purchase executive', 240), frozen: true },
        rc.money('estimatedAmount', 'Estimated amount'), rc.money('orderedAmount', 'Ordered amount'),
        rc.signed('saving', 'Saved amount'),
        { key: 'savingPct', label: 'Savings %', type: 'pct', width: 120, align: 'right', signed: true },
      ],
    },
    { key: 'detailed', label: 'Detailed' },
  ],
  drill: { by: 'document', key: 'id', label: 'The order behind this' },
  help: 'Purchase and subcontract orders, approved or in review. The estimated rate is the rate on the line the order was raised from (the estimate or the request), else the item\'s standard cost, else the ordered rate — so a line with no estimate saves nothing rather than inventing a saving. Savings % is saved amount over estimated amount.',
} as const)

const PRICE_DOC_TYPES = ['purchase_order', 'supplier_quotation', 'subcontract_order', 'supplier_rate_contract'] as const

export const purchasePriceHistory = defineReport({
  key: 'purchase_price_history', route: 'purchase_price_history', group: 'Operation',
  label: 'Purchase price records',
  blurb: "Shows material's purchase rate, discount, and amount. Analyze the purchases by item, supplier, purchase date, and item categories.",
  // Item, supplier, From date or category — any one of them (RPT-076,
  // REP3-002). Choosing an item makes the category and brand moot, so they
  // are switched off. A From date asks for a To date.
  filters: [
    select('docType', 'Document type', [['', 'All'], ...PRICE_DOC_TYPES.map((k) => [k, labelOf(k)] as [string, string])]),
    { ...item(), requiredGroup: 'what', disables: ['categoryId', 'brand'] },
    { ...rf.supplier(), requiredGroup: 'what' },
    { ...rf.fromTo({ requireTo: false, toFollowsFrom: true }, false), requiredGroup: 'what', defaultPeriod: 'none' },
    { ...rf.category(), multiple: true, requiredGroup: 'what' },
    brand(),
  ],
  columns: [
    { ...rc.text('itemName', 'Item', 220), frozen: true }, rc.text('description', 'Description', 220),
    rc.date('documentDate', 'Document date'), rc.text('supplierName', 'Supplier', 220),
    rc.text('brand', 'Brand', 130), rc.text('model', 'Model', 130),
    rc.qty('quantity', 'Quantity'), rc.text('unit', 'Unit', 90), rate('rate', 'Rate'),
    rate('rateFcy', 'Rate (FCY)'),
    { key: 'discountPct', label: 'Discount', type: 'pct', width: 110, align: 'right' },
    rate('finalRate', 'Final rate'), rate('finalRateFcy', 'Final rate (FCY)'),
    rc.money('amount', 'Amount'), rc.text('paymentTerms', 'Payment term', 200),
    rc.doc(), pick('docTypeLabel', 'Document type', 180), pick('itemCategory', 'Item category', 160),
  ],
  drill: { by: 'document', key: 'id', label: 'The document behind this' },
} as const)

export const materialPurchaseForecast = defineReport({
  key: 'material_purchase_forecast', route: 'material_purchase_forecast', group: 'Operation',
  label: 'Material purchase forecast',
  blurb: 'Shows the list of materials that you might require in a particular period along with their estimated amounts.',
  // A window or a project, either will do; a From date asks for a To date (REP3-004).
  filters: [
    { ...rf.fromTo({ requireTo: false, toFollowsFrom: true }, false), requiredGroup: 'scope', defaultPeriod: 'none' },
    { ...rf.project(), requiredGroup: 'scope' }, rf.category(),
  ],
  columns: [
    rc.text('itemName', 'Material', 220), rc.text('description', 'Description', 220), rc.text('unit', 'Unit', 90),
    rc.qty('requestedQty', 'Net requested qty'), rc.qty('fulfilledQty', 'Fulfilled qty'),
    rc.qty('toBuyQty', 'To be purchased qty', true), rate('estimatedRate', 'Estimated rate'),
    rc.money('toBuyValue', 'Est purchase amount'),
    rc.date('requiredDate', 'Required date'), rc.project(),
    { ...rc.doc('documentNo', 'Mat req no'), link: { to: 'document', idKey: 'id' } },
  ],
  help: 'One row per material request line required in the window — or, with only a project, every open material request line of the project. Net requested is the requested quantity less anything pre-closed; fulfilled is what has been ordered (directly or through a requisition) or transferred from stock against it. The estimated rate is the rate on the estimate the request was picked from, else the request\'s own rate, else the item\'s standard cost.',
} as const)

export const employeeAdvances = defineReport({
  key: 'employee_advances', route: 'employee_advances_expenses', group: 'Operation',
  label: 'Employee advances and expenses',
  blurb: "A report to view employees' outstanding advances and their expenses.",
  filters: [
    rf.employee(), { ...rf.period('Document date', false), defaultPeriod: 'last12Months' }, rf.project(),
    select('relatedTo', 'Related to', [['PROJECT', 'Project'], ['SERVICE', 'Service']]),
  ],
  columns: [
    rc.text('employeeName', 'Employee', 220), rc.text('teamName', 'Team', 160),
    rc.doc(), rc.date('documentDate', 'Date'), rc.project(), pick('relatedTo', 'Related to', 130),
    rc.text('expenseHead', 'Expense head', 200), rc.text('description', 'Description', 240),
    rc.date('incurredOn', 'Incurred on'), rc.money('amount', 'Amount'),
  ],
  views: [
    { key: 'expenses', label: 'Expenses' },
    {
      key: 'advances', label: 'Outstanding advances',
      columns: [
        rc.text('employeeName', 'Employee', 220), rc.text('teamName', 'Team', 160),
        rc.doc(), rc.date('documentDate', 'Date'), rc.project(), pick('relatedTo', 'Related to', 130),
        rc.money('advance', 'Advance'), rc.money('settled', 'Settled'), rc.signed('outstanding', 'Outstanding'),
      ],
    },
  ],
  help: 'Expenses lists every line of every expense report. Outstanding advances lists each advance payment with what has been settled against it on later expense reports (approved or in review); outstanding is the advance less that.',
} as const)

/* ============================================================== Inventory */

const STOCK_MODE = rf.mode([['WAREHOUSE', 'Warehouses'], ['PROJECT', 'Projects'], ['ITEM', 'Materials']], true)
const BY_WAREHOUSE = { key: 'mode', values: ['WAREHOUSE'] } as const
const BY_PROJECT = { key: 'mode', values: ['PROJECT'] } as const

export const stockAvailability = defineReport({
  key: 'stock_availability', route: 'stock_availability', group: 'Inventory',
  label: 'Stock availability',
  blurb: 'Shows stock available in a warehouse or project site. Also check availability of a material across all stores.',
  // Warehouse under Show by Warehouses, Project under Show by Projects (REP3-005).
  filters: [
    { ...STOCK_MODE, defaultValue: 'WAREHOUSE' },
    { ...rf.warehouse(), requiredWhen: BY_WAREHOUSE, visibleWhen: BY_WAREHOUSE },
    { ...rf.project(), requiredWhen: BY_PROJECT, visibleWhen: BY_PROJECT }, { ...rf.includeSub(), visibleWhen: BY_PROJECT },
    item(), rf.category(), brand(),
    select('itemStatus', 'Status', [['ACTIVE', 'Active'], ['INACTIVE', 'Inactive'], ['ALL', 'All']], { defaultValue: 'ACTIVE' }),
  ],
  columns: [
    { ...rc.text('locationName', 'Warehouse / project', 220), frozen: true },
    rc.text('itemName', 'Item', 240), rc.text('brand', 'Brand', 130), rc.text('model', 'Model no', 120),
    rc.text('unit', 'Unit', 90),
    rc.qty('onHandStock', 'Stock', true), rc.qty('onHandProject', 'Project', true), rc.qty('onHand', 'Total', true),
    pick('itemCategory', 'Item category', 160),
    details(),
    { ...rc.text('bins', 'Bins', 180), optional: true },
  ].map((c) => (['onHandStock', 'onHandProject', 'onHand'].includes(c.key) ? { ...c, group: 'On hand' } : c)),
  drill: {
    by: 'item', key: 'itemId',
    docTypes: ['goods_receipt', 'material_transfer', 'stock_adjustment', 'warehouse_transfer', 'delivery_challan'],
    label: 'What moved this stock',
  },
  help: 'On hand — Stock is free stock; On hand — Project is stock held for a project (received against a project order, or at a site). Show by Warehouses groups by store, Projects by the project the stock is held for, Materials across every store.',
} as const)

export const stockPurchasePlanner = defineReport({
  key: 'stock_purchase_planner', route: 'stock_purchase_planner', group: 'Inventory',
  label: 'Stock purchase planner',
  blurb: 'Shows the list of items with stock levels below the reorder point, and where stock is short of what open projects will need.',
  filters: [
    select('show', 'Show', [['PENDING', 'Pending'], ['ALL', 'All']], { required: true, defaultValue: 'PENDING' }),
    { ...rf.warehouse(true), multiple: true }, item(), { ...rf.category(), multiple: true },
  ],
  columns: [
    rc.text('itemName', 'Material', 240), rc.qty('onHand', 'On hand'),
    rc.qty('committed', 'Committed'),
    // docs/15 R-8.6 — the level the item master sets, so the buyer can see
    // which of the two reasons put this row in front of them.
    rc.qty('reorderPoint', 'Reorder point'),
    rc.qty('shortfall', 'Shortfall'),
    rc.text('reason', 'Why', 200),
  ],
  help: 'Pending lists only materials with a shortfall; All lists every material held in the chosen warehouses or needed by a project, short or not.',
} as const)

export const stockPrExecution = defineReport({
  key: 'stock_pr_execution', route: 'stock_pr_exec_status', group: 'Inventory',
  label: 'Stock PR execution',
  blurb: 'Shows the purchase status of materials in stock purchase requests.',
  filters: [
    select('purchaseStatus', 'Purchase status', [['ALL', 'All'], ['OPEN', 'Open'], ['PURCHASED', 'Fully purchased']],
      { defaultValue: 'OPEN' }),
    rf.warehouse(),
    { ...keyedRange('approvedDate', 'Approved date', 'approvedFrom', 'approvedTo'), requiredGroup: 'when' },
    { ...rf.period('Document date', false), defaultPeriod: 'none', requiredGroup: 'when' },
    lookup('stockPurchaseRequestId', 'Stock purchase request', 'stock_purchase_request', { requiredGroup: 'when' }),
  ],
  columns: [
    { ...rc.doc(), frozen: true }, rc.date('documentDate', 'Raised'), rc.date('approvedDate', 'Approved'),
    rc.text('warehouseName', 'Warehouse', 180), rc.text('itemName', 'Material', 220), rc.text('unit', 'Unit', 90),
    rc.qty('requestedQty', 'Requested'),
    drillQty('orderedQty', 'Ordered', ['purchase_order']),
    rc.qty('preClosedQty', 'Pre-closed'), rc.qty('pendingQty', 'Open'),
    details(),
  ],
  drill: { by: 'line', key: 'lineId', docTypes: ['purchase_order', 'goods_receipt'], label: 'Orders against this request' },
} as const)

export const materialInwardOutward = defineReport({
  key: 'material_inward_outward', route: 'material_inward_outward', group: 'Inventory',
  label: 'Material inward and outward',
  blurb: 'Shows all the materials movement in and out of a warehouse and project site, date-wise.',
  // Show by opens blank — every movement, as Materials (REP3-007).
  filters: [
    { ...STOCK_MODE, required: false },
    { ...rf.warehouse(), multiple: true, visibleWhen: BY_WAREHOUSE },
    { ...rf.project(), multiple: true, visibleWhen: BY_PROJECT }, { ...rf.includeSub(), visibleWhen: BY_PROJECT },
    { ...rf.period('Period'), defaultPeriod: 'last12Months' },
    item(), { key: 'serialNo', label: 'Serial no', control: 'text' },
    { ...rf.category(), multiple: true }, brand(),
  ],
  columns: [
    rc.date('documentDate', 'Date'), rc.doc(), pick('docTypeLabel', 'Type', 190),
    rc.text('locationName', 'Warehouse / project', 200),
    rc.text('itemName', 'Material', 220), rc.text('brand', 'Brand', 130), rc.text('unit', 'Unit', 90),
    rc.qty('inQty', 'In', true), rc.qty('outQty', 'Out', true),
    { ...rc.text('serialNo', 'Serial no', 150), optional: true },
  ],
  // Show by is optional now; the reference still waits for Apply.
  runOnOpen: false,
  help: 'Show by Warehouses lists movements in and out of stores, Projects movements at sites and stock held for a project, Materials (or no choice) every movement wherever it happened.',
} as const)

export const stockValuation = defineReport({
  key: 'stock_valuation', route: 'stock_valuation', group: 'Inventory',
  label: 'Stock valuation',
  blurb: 'Shows the value of all the materials available in the warehouse or project.',
  filters: [
    { ...rf.mode([['WAREHOUSE', 'Warehouses'], ['PROJECT', 'Projects']], true), defaultValue: 'WAREHOUSE' },
    { ...rf.warehouse(), multiple: true, requiredWhen: BY_WAREHOUSE, visibleWhen: BY_WAREHOUSE },
    { ...rf.project(), multiple: true, visibleWhen: BY_PROJECT }, { ...rf.includeSub(), visibleWhen: BY_PROJECT },
    { ...rf.period('Document date', true), defaultPeriod: 'lastMonth' },
    item(), { ...rf.category(), multiple: true },
  ],
  columns: [
    { ...rc.text('locationName', 'Warehouse / project', 220), frozen: true },
    rc.text('itemName', 'Item', 240), rc.text('brand', 'Brand', 130), pick('itemCategory', 'Item category', 160),
    rc.text('unit', 'Unit', 90),
    rc.qty('openingQty', 'Opening qty'), rc.qty('inwardQty', 'Total inward'), rc.qty('outwardQty', 'Total outward'),
    rc.qty('closingQty', 'Closing qty'), rate('closingRate', 'Closing rate'), rc.money('closingValue', 'Closing value'),
    details(),
  ],
  drill: {
    by: 'item', key: 'itemId',
    docTypes: ['goods_receipt', 'material_transfer', 'stock_adjustment', 'warehouse_transfer', 'delivery_challan',
      'site_inventory_record', 'material_consumption', 'debit_note'],
    label: 'What moved this stock',
  },
  help: 'Opening is what was on hand at the start of the period; inward is what was received in it and outward what was taken off it; closing is opening + inward − outward. The closing value is what is left on each receipt at its own rate, and the closing rate is that value over the closing quantity.',
} as const)

export const stockAgeing = defineReport({
  key: 'stock_ageing', route: 'stock_ageing', group: 'Inventory',
  label: 'Stock ageing',
  blurb: 'Shows the age of the general stock currently available in the warehouse.',
  filters: [{ ...rf.warehouse(true), multiple: true }, item(), { ...rf.category(), multiple: true }],
  columns: [
    rc.text('itemName', 'Material'), rc.text('warehouseName', 'Warehouse', 200),
    rc.qty('onHand', 'On hand'), rc.date('lastReceived', 'Last received'),
    { key: 'ageDays', label: 'Age (days)', type: 'text', width: 130, align: 'right' },
  ],
} as const)

/* ================================================================== Tools */

export const toolAvailability = defineReport({
  key: 'tool_availability', route: 'tool_availability', group: 'Tools and Equipment',
  label: 'Tool availability',
  blurb: 'Track the current availability status of tools',
  filters: [
    lookup('toolCategoryId', 'Tool category', 'tool_category'), rf.warehouse(),
    { key: 'includePersonalToolkit', label: 'Include personal toolkit', control: 'checkbox' },
  ],
  columns: [
    rc.text('itemName', 'Tool'), rc.text('warehouseName', 'Warehouse', 200),
    rc.qty('addedQty', 'In inventory', true), rc.qty('issuedQty', 'Issued out', true),
    rc.qty('availableQty', 'Available', true),
  ],
  help: 'Tools issued into somebody\'s personal toolkit stay with that person and are left out of Issued out unless Include personal toolkit is ticked.',
} as const)

/* ============================================================ Integration */

const SYNC_TONES: Readonly<Record<string, ReportTone>> = {
  Completed: 'good', Pending: 'warn', Stopped: 'bad', Failed: 'bad', 'Not applicable': 'muted', 'Out of scope': 'muted',
}

export const integrationStatus = defineReport({
  key: 'integration_status', route: 'tally_sync_status', group: 'Integration',
  label: 'ERP / accounting integration status',
  blurb: 'A report to view the integration status of invoices and Expense Reports.',
  // Opens on Pending with no dates; All asks for both dates (REP3-010).
  filters: [
    select('integrationStatus', 'Integration sync status', [
      ['ALL', 'All'], ['COMPLETED', 'Completed'], ['PENDING', 'Pending'], ['STOPPED', 'Stopped'],
      ['FAILED', 'Failed'], ['NOT_APPLICABLE', 'Not applicable'],
    ], { required: true, defaultValue: 'PENDING' }),
    { ...rf.fromTo({}, false), requiredWhen: { key: 'integrationStatus', values: ['ALL'] }, defaultPeriod: 'none' },
    rf.docType(SYNCABLE_DOC_TYPES.map((k) => ({ value: k, label: labelOf(k) }))),
    rf.status(),
  ],
  columns: [
    pick('docTypeLabel', 'Document type', 190), rc.doc(), rc.date('documentDate', 'Document date'),
    { ...rc.text('syncStatus', 'Integration sync status', 190), tones: SYNC_TONES },
    { ...rc.status(), optional: true },
    rc.text('integrationRef', 'Reference in the books', 190),
    { ...rc.text('integrationError', 'Last error', 260), optional: true },
    { ...rc.money('total', 'Value'), optional: true },
  ],
  views: [
    { key: 'detailed', label: 'Detailed' },
    {
      key: 'logs', label: 'Logs',
      columns: [
        { key: 'at', label: 'When', type: 'date', width: 130 },
        rc.text('atTime', 'Time', 100),
        pick('docTypeLabel', 'Document type', 190), rc.doc(),
        { ...rc.text('outcome', 'Result', 150), tones: { Completed: 'good', Failed: 'bad', Stopped: 'bad', Requeued: 'warn' } },
        pick('connector', 'Through', 130), rc.text('reference', 'Reference', 180),
        rc.text('error', 'Message', 300), rc.text('byName', 'By', 160),
      ],
    },
  ],
} as const)
